/**
 * THE SHELF: how far a patch of water is from the land beside it, and WHAT that land is made of.
 *
 * CONTRACT 1.5w. The three renders the owner named all read the same way at the shore and it is
 * not the tile the game draws: the water LIGHTENS towards land over a long soft distance, and it
 * lightens differently at a beach than at a cliff. woolstrand-8302 runs turquoise for a couple of
 * hundred pixels off a sand beach with white foam at the waterline; blackstage-8325's water goes
 * pale right up to bare rock with no foam at all; almadra-8349 is a pale ring inside a limestone
 * rim. One treatment on every shoreline would be wrong on two of the three, which is the oldest
 * structural failure in this project's ledger ("one richer treatment applied everywhere").
 *
 * -- WHY IT HANGS OFF THE SPRITE'S OWN PIXELS ---------------------------------------------------
 *
 * The alternative is the stadium `water.ts` already has, and it is not good enough for a TONE: a
 * stadium hung on Murrin Roost's 2,483 x 715 box stands about 900 art px off its real shore at the
 * mouth of the bay, so the pale shelf would sit on open water and the water against the quay would
 * be deep. That is worse than today's sea at the exact camera the owner judges. The distance here
 * is to the SILHOUETTE, so a bay is a bay.
 *
 * It is also PURE IN THE SPRITE'S OWN PIXELS - the same bytes `contactBand` reads - so it is built
 * once in `buildLoaded`, accrues nothing and cannot differ between two tabs.
 *
 * -- THE LATTICE, AND WHAT IT COSTS -------------------------------------------------------------
 *
 * One sample per SHELF_STEP_PX art px, over the sprite's box grown by the shelf's own reach, and a
 * Danielsson nearest-source propagation (two passes, four neighbours each) rather than a chamfer
 * of distances alone - because the material byte is an ARGMIN and a chamfer does not carry one.
 * The result is collapsed to two arrays and the propagation buffer is dropped:
 *
 *   distPx    Uint16, art px to the nearest land pixel, clamped at shelfPx
 *   material  Uint8, the material of THAT pixel (sand / lip / rock)
 *
 * DERIVED, at shelfPx 184 and step 4: a 250 x 257 islet costs 15,200 samples = 45.6 KB; Murrin
 * Roost's 2,483 x 715 costs 165,000 = 495 KB. Against the settled arm's MEASURED 4,718,592 B of
 * recoloured water sheets, which the render arm does not build.
 *
 * -- THE MATERIALS, MEASURED off the baked sprites ----------------------------------------------
 *
 * `work`-run over every silhouette-boundary pixel of all 27 baked islands (n = 7,388 on Murrin
 * Roost alone). The generated islets are dark to the waterline: boundary luma p50 runs 39.5 to
 * 78.9 across 25 of them and the fraction above luma 140 is 0.000 to 0.028. The two hand-authored
 * islands are the exception, and they are the only two with a pale shore: Murrin Roost p50 106.6
 * with 36.7% of its boundary above 140 (hue p50 46 deg, warm), Torsk Vard p50 107.4 with 31.2%
 * above 140 (sat p50 0.03 - grey, so its hue is noise and it must not be read).
 *
 * So the cut is on LUMA first and hue only where there is saturation to carry it:
 *
 *   ROCK  luma < 90              25 of 27 sprites are almost entirely this
 *   SAND  luma >= 140, sat >= 0.06, hue 15..95 deg     the warm pale shore, Murrin's quay stone
 *   LIP   everything else        pale grey stone, and the vegetated edge between the two
 */

import type { Rgb } from './palette';
import { rgbToHsv, luma } from './palette';

/** One sample per this many art px. GUESS: 4 is the finest a 2,483 px sprite can afford. */
export const SHELF_STEP_PX = 4;

export const SHELF_SAND = 0;
export const SHELF_LIP = 1;
export const SHELF_ROCK = 2;
export type ShelfMaterial = 0 | 1 | 2;

/** MEASURED cuts; see the header for the n and the percentiles behind each. */
export const SHELF_SAND_MIN_LUMA = 140;
export const SHELF_SAND_MIN_SAT = 0.06;
export const SHELF_SAND_HUE = [15, 95] as const;
export const SHELF_ROCK_MAX_LUMA = 90;

/** What one land pixel is made of. Pure in the pixel. */
export function materialOf(c: Rgb): ShelfMaterial {
  const L = luma(c);
  if (L < SHELF_ROCK_MAX_LUMA) return SHELF_ROCK;
  const { h, s } = rgbToHsv(c);
  if (L >= SHELF_SAND_MIN_LUMA && s >= SHELF_SAND_MIN_SAT && h >= SHELF_SAND_HUE[0] && h <= SHELF_SAND_HUE[1]) return SHELF_SAND;
  return SHELF_LIP;
}

export interface ShelfField {
  /** Samples across and down. */
  readonly w: number;
  readonly h: number;
  /** Sprite-space art px of sample (0, 0). Negative: the field reaches outside the sprite's box. */
  readonly x0: number;
  readonly y0: number;
  readonly stepPx: number;
  /** The reach this field was built for. Anything further away reads exactly this. */
  readonly shelfPx: number;
  /** Art px to the nearest land pixel, clamped at `shelfPx`. 0 inside the silhouette. */
  readonly distPx: Uint16Array;
  /** The material of the land pixel that distance was to. SHELF_ROCK where there is no land in reach. */
  readonly material: Uint8Array;
  /**
   * WHICH WAY THE LAND IS: the direction from this sample to the land pixel `distPx` measured
   * to, as a turn in 0..255 (one turn is 256, so a step is 1.41 deg). 0 where there is no land
   * in reach and inside the silhouette, where it has no meaning.
   *
   * THIS BYTE IS WHAT LETS THE CREST RUN ALONG A COAST (CONTRACT 1.5y). Distance alone can only
   * say HOW MUCH crest a patch of water gets; it cannot say which way a mark should lie, and the
   * ruling is that near a shore the marks "run along it rather than across it". The Danielsson
   * pass above already carries the nearest land SAMPLE, so the vector is free at build time and
   * would cost a second field to recover afterwards - the gradient of a quantised distance map is
   * eight-directional and useless at this step. Quantised to a byte rather than kept as two
   * floats because it is a DIRECTION, 1.41 deg is far finer than an 8 px crest cell can express,
   * and two Float32s would triple this field's memory: DERIVED at shelfPx 96 and step 4, Murrin
   * Roost's 2,483 x 715 sprite holds 165,000 samples, so the byte is 165 KB and the pair 1.3 MB.
   */
  readonly dirTurn: Uint8Array;
  /**
   * 1 where EVERY art pixel of this lattice sample's step x step block is inside the sprite AND
   * is silhouette. Not "is land" - "is SOLID land, with no hole and no edge in it".
   *
   * THE SEA IS NOT COMPUTED WHERE THE SEA CANNOT BE SEEN. A water cell whose whole square is
   * under solid silhouette is drawn and then painted over by the sprite, and at Torsk Vard -
   * 1,756 x 742 art px, the body this round's cost bar reads worst - that is a third of the
   * frame. This byte is what lets the crest skip it, and it is deliberately conservative: any
   * block containing one transparent pixel, or reaching outside the sprite at all, is 0.
   */
  readonly solid: Uint8Array;
}

/**
 * The shelf field of one sprite. `mask` is the silhouette (1 = land), `rgba` its pixels.
 *
 * The lattice sample at (i, j) stands for the art pixel at its own position, and a lattice cell
 * counts as LAND when any art pixel of its step x step block is land - a shore is where the land
 * begins, and rounding a spit of rock away would put deep water against it.
 */
export function buildShelf(mask: Uint8Array, rgba: Uint8ClampedArray | Uint8Array, w: number, h: number, shelfPx: number): ShelfField {
  const step = SHELF_STEP_PX;
  const margin = Math.ceil(shelfPx / step) + 1;
  const gw = Math.ceil(w / step) + 2 * margin;
  const gh = Math.ceil(h / step) + 2 * margin;
  const x0 = -margin * step;
  const y0 = -margin * step;
  const n = gw * gh;

  // ---- the lattice: land, and what it is made of --------------------------------------------
  const isLand = new Uint8Array(n);
  const mat = new Uint8Array(n);
  const solid = new Uint8Array(n);
  for (let j = 0; j < gh; j++) {
    for (let i = 0; i < gw; i++) {
      const sx = x0 + i * step;
      const sy = y0 + j * step;
      let counts0 = 0;
      let counts1 = 0;
      let counts2 = 0;
      let inBounds = 0;
      for (let dy = 0; dy < step; dy++) {
        const y = sy + dy;
        if (y < 0 || y >= h) continue;
        for (let dx = 0; dx < step; dx++) {
          const x = sx + dx;
          if (x < 0 || x >= w) continue;
          inBounds++;
          const q = y * w + x;
          if (!mask[q]) continue;
          const m = materialOf([rgba[q * 4]!, rgba[q * 4 + 1]!, rgba[q * 4 + 2]!]);
          if (m === SHELF_SAND) counts0++;
          else if (m === SHELF_LIP) counts1++;
          else counts2++;
        }
      }
      const total = counts0 + counts1 + counts2;
      if (total === 0) continue;
      isLand[j * gw + i] = 1;
      if (total === step * step && inBounds === step * step) solid[j * gw + i] = 1;
      // The modal material of the block, sand first on a tie: a beach one pixel wide is still a beach.
      mat[j * gw + i] = counts0 >= counts1 && counts0 >= counts2 ? SHELF_SAND : counts1 >= counts2 ? SHELF_LIP : SHELF_ROCK;
    }
  }

  // ---- Danielsson: propagate the NEAREST LAND SAMPLE, not just its distance ------------------
  // Two passes, four neighbours each. `near` holds the index of the nearest land sample so far,
  // which is what carries the material byte through; -1 is "nothing found yet".
  const near = new Int32Array(n).fill(-1);
  for (let q = 0; q < n; q++) if (isLand[q]) near[q] = q;
  const d2 = (q: number, s: number): number => {
    const dx = (q % gw) - (s % gw);
    const dy = ((q / gw) | 0) - ((s / gw) | 0);
    return dx * dx + dy * dy;
  };
  const relax = (q: number, p: number): void => {
    const s = near[p]!;
    if (s < 0) return;
    const cur = near[q]!;
    if (cur < 0 || d2(q, s) < d2(q, cur)) near[q] = s;
  };
  for (let j = 0; j < gh; j++) {
    for (let i = 0; i < gw; i++) {
      const q = j * gw + i;
      if (i > 0) relax(q, q - 1);
      if (j > 0) relax(q, q - gw);
      if (j > 0 && i > 0) relax(q, q - gw - 1);
      if (j > 0 && i + 1 < gw) relax(q, q - gw + 1);
    }
  }
  for (let j = gh - 1; j >= 0; j--) {
    for (let i = gw - 1; i >= 0; i--) {
      const q = j * gw + i;
      if (i + 1 < gw) relax(q, q + 1);
      if (j + 1 < gh) relax(q, q + gw);
      if (j + 1 < gh && i + 1 < gw) relax(q, q + gw + 1);
      if (j + 1 < gh && i > 0) relax(q, q + gw - 1);
    }
  }

  const distPx = new Uint16Array(n);
  const material = new Uint8Array(n);
  const dirTurn = new Uint8Array(n);
  const cap = Math.round(shelfPx);
  for (let q = 0; q < n; q++) {
    const s = near[q]!;
    if (s < 0) {
      distPx[q] = cap;
      material[q] = SHELF_ROCK;
      continue;
    }
    const dx = (q % gw) - (s % gw);
    const dy = ((q / gw) | 0) - ((s / gw) | 0);
    distPx[q] = Math.min(cap, Math.round(Math.hypot(dx, dy) * step));
    material[q] = mat[s]!;
    // The vector points FROM the water TO the land, so `shelfNormalAt` below is the inward
    // normal and its perpendicular is the tangent the crest runs along.
    dirTurn[q] = dx === 0 && dy === 0 ? 0 : (Math.round((Math.atan2(dy, dx) / (2 * Math.PI)) * 256) & 255);
  }
  return { w: gw, h: gh, x0, y0, stepPx: step, shelfPx: cap, distPx, material, dirTurn, solid };
}

/**
 * IS THE WHOLE SQUARE [sx, sx + sizePx) x [sy, sy + sizePx) UNDER SOLID SILHOUETTE?
 *
 * Sprite-space art px. Conservative in every direction: a square that reaches outside the field,
 * or that touches one lattice block that is not `solid`, answers false. Used only to SKIP work
 * whose result would be painted over, so a false negative costs a little time and a false
 * positive would cost a hole in the sea - which is why it rounds outward.
 */
export function shelfSolidSquare(f: ShelfField, sx: number, sy: number, sizePx: number): boolean {
  const i0 = Math.floor((sx - f.x0) / f.stepPx);
  const j0 = Math.floor((sy - f.y0) / f.stepPx);
  const i1 = Math.floor((sx + sizePx - 1 - f.x0) / f.stepPx);
  const j1 = Math.floor((sy + sizePx - 1 - f.y0) / f.stepPx);
  if (i0 < 0 || j0 < 0 || i1 >= f.w || j1 >= f.h) return false;
  for (let j = j0; j <= j1; j++) {
    for (let i = i0; i <= i1; i++) if (!f.solid[j * f.w + i]) return false;
  }
  return true;
}

export interface ShelfSample {
  /** Art px to the nearest land pixel of this sprite, clamped at the field's `shelfPx`. */
  readonly distPx: number;
  readonly material: ShelfMaterial;
}

/** Far water: what a point outside the field reads as. */
const AWAY = { distPx: Number.POSITIVE_INFINITY, material: SHELF_ROCK as ShelfMaterial } as const;

/**
 * The field at a point in the SPRITE'S OWN art-pixel space (0,0 is the sprite's top-left). Nearest
 * sample, never interpolated: an interpolated distance would put a gradient inside a tone band and
 * 1.5e forbids the gradient, not the step.
 */
export function shelfAt(f: ShelfField | null, sx: number, sy: number): ShelfSample {
  if (f === null) return AWAY;
  const i = Math.round((sx - f.x0) / f.stepPx);
  const j = Math.round((sy - f.y0) / f.stepPx);
  if (i < 0 || j < 0 || i >= f.w || j >= f.h) return AWAY;
  const q = j * f.w + i;
  return { distPx: f.distPx[q]!, material: f.material[q]! as ShelfMaterial };
}

/** The 256 turns, once. A lookup rather than an `atan2` inverse at every crest corner. */
const TURN_COS = new Float64Array(256);
const TURN_SIN = new Float64Array(256);
for (let t = 0; t < 256; t++) {
  TURN_COS[t] = Math.cos((t / 256) * 2 * Math.PI);
  TURN_SIN[t] = Math.sin((t / 256) * 2 * Math.PI);
}

/**
 * THE INWARD NORMAL at a point in the sprite's own art-pixel space: the unit vector from the
 * water towards the nearest land pixel, as `[nx, ny]`. `[0, 0]` where there is no land in reach,
 * which is the ONE value a caller must branch on - a zero normal has no tangent.
 *
 * Nearest sample and never interpolated, for `shelfAt`'s reason: this is read on the crest
 * lattice and an interpolated direction would rotate smoothly across a cell boundary while the
 * distance beside it stepped, which puts a shear in the marks exactly where the shore turns.
 */
export function shelfNormalAt(f: ShelfField | null, sx: number, sy: number): readonly [number, number] {
  if (f === null) return [0, 0];
  const i = Math.round((sx - f.x0) / f.stepPx);
  const j = Math.round((sy - f.y0) / f.stepPx);
  if (i < 0 || j < 0 || i >= f.w || j >= f.h) return [0, 0];
  const q = j * f.w + i;
  if (f.distPx[q]! >= f.shelfPx) return [0, 0];
  const t = f.dirTurn[q]!;
  return [TURN_COS[t]!, TURN_SIN[t]!];
}

/**
 * THE DISTANCE AND THE INWARD NORMAL TOGETHER, written into a caller's buffer at `at`, `at + 1`
 * and `at + 2`. One lattice index for the three, because the renderer takes this per crest
 * corner and the distance and the direction must come from the SAME sample or a mark can be
 * placed by one shore and turned by another. Returns the distance for the caller's own compare.
 */
export function shelfProbeInto(f: ShelfField, sx: number, sy: number, out: Float32Array | Float64Array, at: number): number {
  const i = Math.round((sx - f.x0) / f.stepPx);
  const j = Math.round((sy - f.y0) / f.stepPx);
  if (i < 0 || j < 0 || i >= f.w || j >= f.h) return Number.POSITIVE_INFINITY;
  const q = j * f.w + i;
  const d = f.distPx[q]!;
  const t = f.dirTurn[q]!;
  out[at] = d;
  out[at + 1] = d >= f.shelfPx ? 0 : TURN_COS[t]!;
  out[at + 2] = d >= f.shelfPx ? 0 : TURN_SIN[t]!;
  return d;
}

/**
 * THE DISTANCE ALONE, without the object. The renderer asks this 2,135 times a draw for every
 * sprite on screen and never wants the material; `shelfAt` above is the readable form and is
 * what the tests use.
 */
export function shelfDistAt(f: ShelfField, sx: number, sy: number): number {
  const i = Math.round((sx - f.x0) / f.stepPx);
  const j = Math.round((sy - f.y0) / f.stepPx);
  if (i < 0 || j < 0 || i >= f.w || j >= f.h) return Number.POSITIVE_INFINITY;
  return f.distPx[j * f.w + i]!;
}
